import * as THREE from "three";

export const CAMERA_KEY_BINDINGS_STORAGE_KEY = "eagle-eye.camera-key-bindings.v1";

export const DEFAULT_CAMERA_KEY_BINDINGS = Object.freeze({
  rotate: "RIGHT",
  pan: "MIDDLE",
  panModifier: "CTRL_OR_META",
  zoom: "WHEEL",
  zoomIn: "KEY:Equal",
  zoomOut: "KEY:Minus",
});

const MOUSE_BINDINGS = new Set(["LEFT", "MIDDLE", "RIGHT"]);
const MODIFIER_BINDINGS = new Set(["CTRL_OR_META", "CTRL", "META", "SHIFT", "ALT"]);

export const normalizeCameraKeyBindings = (value) => {
  const bindings = { ...DEFAULT_CAMERA_KEY_BINDINGS };
  if (!value || typeof value !== "object") return bindings;

  if (MOUSE_BINDINGS.has(value.rotate) || String(value.rotate || "").startsWith("KEY:")) bindings.rotate = value.rotate;
  if (MOUSE_BINDINGS.has(value.pan) || String(value.pan || "").startsWith("KEY:")) bindings.pan = value.pan;
  if (MODIFIER_BINDINGS.has(value.panModifier) || String(value.panModifier || "").startsWith("KEY:")) bindings.panModifier = value.panModifier;
  if (String(value.zoomIn || "").startsWith("KEY:")) bindings.zoomIn = value.zoomIn;
  if (String(value.zoomOut || "").startsWith("KEY:")) bindings.zoomOut = value.zoomOut;
  return bindings;
};

export const loadCameraKeyBindings = () => {
  if (typeof window === "undefined") return { ...DEFAULT_CAMERA_KEY_BINDINGS };

  try {
    return normalizeCameraKeyBindings(JSON.parse(window.localStorage.getItem(CAMERA_KEY_BINDINGS_STORAGE_KEY)));
  } catch {
    return { ...DEFAULT_CAMERA_KEY_BINDINGS };
  }
};

export const saveCameraKeyBindings = (bindings) => {
  const normalized = normalizeCameraKeyBindings(bindings);
  if (typeof window !== "undefined") {
    window.localStorage.setItem(CAMERA_KEY_BINDINGS_STORAGE_KEY, JSON.stringify(normalized));
  }
  return normalized;
};

export const applyCameraKeyBindings = (controls, bindings = loadCameraKeyBindings()) => {
  if (!controls) return;
  const normalized = normalizeCameraKeyBindings(bindings);
  const mouseAction = (button) => {
    if (normalized.rotate === button) return THREE.MOUSE.ROTATE;
    if (normalized.pan === button) return THREE.MOUSE.PAN;
    return null;
  };
  controls.mouseButtons = {
    LEFT: mouseAction("LEFT"),
    MIDDLE: mouseAction("MIDDLE"),
    RIGHT: mouseAction("RIGHT"),
  };
  controls.keyActionBindings = {
    rotate: String(normalized.rotate || "").startsWith("KEY:") ? normalized.rotate.slice(4) : null,
    pan: String(normalized.pan || "").startsWith("KEY:") ? normalized.pan.slice(4) : null,
  };
  controls.keyZoomBindings = {
    in: String(normalized.zoomIn || "").startsWith("KEY:") ? normalized.zoomIn.slice(4) : null,
    out: String(normalized.zoomOut || "").startsWith("KEY:") ? normalized.zoomOut.slice(4) : null,
  };
  controls.panModifier = normalized.panModifier;
};

export const formatCameraBinding = (binding) => ({
  MIDDLE: "Middle mouse button",
  RIGHT: "Right mouse button",
  LEFT: "Left mouse button",
  CTRL_OR_META: "Ctrl / Command",
  CTRL: "Ctrl",
  META: "Command",
  SHIFT: "Shift",
  ALT: "Alt / Option",
  WHEEL: "Mouse wheel",
}[binding] || (String(binding || "").startsWith("KEY:") ? String(binding).slice(4).replace(/^Key/, "").replace(/^Digit/, "") : binding));

export const bindingFromMouseEvent = (event) => ({
  0: "LEFT",
  1: "MIDDLE",
  2: "RIGHT",
}[event.button] || null);

export const bindingFromAnyKeyEvent = (event) => event.code ? `KEY:${event.code}` : null;
